import { __rest } from './../../external/tslib/tslib.es6.js';
import React, { forwardRef, useState, useRef, useImperativeHandle, useCallback, useEffect } from 'react';
import FocusTrap from 'focus-trap-react';
import DropdownBackButton from './DropdownBackButton.js';
import { useDropdown } from './DropdownContext.js';
import { Wrapper, ControlHeight, Menu } from './styles.js';
import { calculateDropdownPosition } from './utils/calculateDropdownPosition.js';
import Portal from '../../hooks/Portal.js';
import useIsLowerResolution from '../../hooks/useIsLowerResolution.js';
import { PORTAL_PREFIX } from '../utils/portalIdPrefix.js';

const PORTAL_ID = `${PORTAL_PREFIX}-dropdown`;
const DropdownComponent = forwardRef((_a, ref) => {
    var { isOpen, anchorElement, onClose, children, direction = 'bottom', isSubMenu, width, maxWidth, minWidth, maxHeight, minHeight, appendTo, returnFocusToPreviousElement = true, focusTrapContainerElements } = _a, props = __rest(_a, ["isOpen", "anchorElement", "onClose", "children", "direction", "isSubMenu", "width", "maxWidth", "minWidth", "maxHeight", "minHeight", "appendTo", "returnFocusToPreviousElement", "focusTrapContainerElements"]);
    const portalId = appendTo || PORTAL_ID;
    const { rootTriggerRef } = useDropdown();
    const isLowerResolution = useIsLowerResolution();
    const [mounted, setMounted] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState({
        top: 0,
        left: 0,
    });
    const menuRef = useRef(null);
    useImperativeHandle(ref, () => menuRef.current);
    const handleClickOutside = (event) => {
        var _a, _b;
        if (onClose &&
            !((_a = menuRef === null || menuRef === void 0 ? void 0 : menuRef.current) === null || _a === void 0 ? void 0 : _a.contains(event.target)) &&
            !((_b = anchorElement.current) === null || _b === void 0 ? void 0 : _b.contains(event.target))) {
            onClose();
        }
    };
    const keypressClose = useCallback((e) => {
        const openedDropdowns = document.querySelectorAll('[data-dropdown]');
        const currentDropdown = openedDropdowns[openedDropdowns.length - 1];
        if (e.key === 'Escape' && currentDropdown === menuRef.current && onClose)
            onClose();
    }, []);
    const calculatePosition = useCallback(() => {
        const reference = isLowerResolution && (rootTriggerRef === null || rootTriggerRef === void 0 ? void 0 : rootTriggerRef.current)
            ? rootTriggerRef.current
            : anchorElement.current;
        if (reference && isOpen && menuRef.current) {
            const { top, left } = calculateDropdownPosition({
                screenWidth: document.documentElement.clientWidth,
                screenHeight: document.documentElement.clientHeight,
                triggerReference: reference,
                contentReference: menuRef.current,
                isLowerResolution,
                direction,
                isSubMenu,
            });
            setDropdownPosition({ top, left });
        }
    }, [
        isOpen,
        isLowerResolution,
        rootTriggerRef === null || rootTriggerRef === void 0 ? void 0 : rootTriggerRef.current,
        anchorElement.current,
        menuRef.current,
    ]);
    useEffect(() => {
        setMounted(true);
    }, []);
    useEffect(() => {
        if (isOpen && menuRef.current && anchorElement.current) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('keydown', keypressClose);
        }
        else {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', keypressClose);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', keypressClose);
        };
    }, [isOpen, anchorElement.current, menuRef.current]);
    useEffect(() => {
        if (!rootTriggerRef.current && isOpen) {
            rootTriggerRef.current = anchorElement.current;
        }
        calculatePosition();
        if (isOpen) {
            document.addEventListener('scroll', calculatePosition);
        }
        return () => {
            document.removeEventListener('scroll', calculatePosition);
        };
    }, [
        isOpen,
        isLowerResolution,
        rootTriggerRef === null || rootTriggerRef === void 0 ? void 0 : rootTriggerRef.current,
        anchorElement.current,
        menuRef.current,
    ]);
    return (React.createElement(React.Fragment, null, mounted && isOpen && (React.createElement(Portal, { id: portalId },
        React.createElement(Wrapper, { onClick: (e) => e.stopPropagation() },
            React.createElement(FocusTrap, { containerElements: focusTrapContainerElements
                    ? [
                        ...focusTrapContainerElements,
                        menuRef.current,
                    ]
                    : undefined, focusTrapOptions: {
                    allowOutsideClick: true,
                    fallbackFocus: anchorElement.current || undefined,
                    returnFocusOnDeactivate: returnFocusToPreviousElement,
                } },
                React.createElement(ControlHeight, { style: {
                        top: (dropdownPosition === null || dropdownPosition === void 0 ? void 0 : dropdownPosition.top) || 'unset',
                        left: (dropdownPosition === null || dropdownPosition === void 0 ? void 0 : dropdownPosition.left) || 'unset',
                    } },
                    React.createElement(Menu, Object.assign({ ref: menuRef, "data-dropdown": true, isSubMenu: isSubMenu, width: width, maxWidth: maxWidth, minWidth: minWidth, maxHeight: maxHeight, minHeight: minHeight, role: "menu" }, props),
                        isSubMenu && isLowerResolution && (React.createElement(DropdownBackButton, { onClick: onClose })),
                        children))))))));
});
DropdownComponent.displayName = 'DropdownComponent';

export { PORTAL_ID, DropdownComponent as default };
